/**
 * mediaAlt — the ONE place a media row's alt text is resolved for a reader.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_media.alt_text_zhHant / _zhHans / _en` is per-locale, and the alt a
 * reader gets must follow the reader's SCRIPT. Three separate defects shipped
 * against that rule (see scripts/lint-photo-alt-locale.ts for the write-up):
 *
 *   1. an endpoint projected the Traditional alt into the `_en` slot, so every
 *      English reader was told the photo was in Chinese;
 *   2. components selected the alt with an `isZh` ternary, which is true for
 *      BOTH Chinese locales — a 简体中文 reader was served the Traditional alt
 *      even when the DB held a correct Simplified one;
 *   3. the shared type had no `photo1AltHans` field at all, so the type
 *      system never noticed a locale column was being dropped.
 *
 * The fix for (2) already exists in this repo: `pickLocalized`
 * (src/cms/pickLocale.ts) picks by script, not by the 'zh' prefix. This
 * module is the thin layer on top of it that knows the media index's shape.
 *
 * EMPTY IS "NOT FILLED IN"
 * -----------------------
 * An empty alt string is not an alt — it means the column was never written.
 * `pickLocalized` treats a blank value as absent and keeps walking the chain,
 * which is exactly right here: most of the live media rows have a Traditional
 * and a Simplified alt but no English one yet.
 *
 * Do NOT reintroduce a `??`/`||` chain over `alt[locale]` here. `alt['en']` is
 * `""` on most rows, and `'' || 'zh-Hant'` is the difference between a
 * described photo and a silent one.
 */
import type { CmsMediaIndexEntry } from '@/content/cms/media.generated'
import { pickLocalized } from './pickLocale'

/** The three locales the media library stores alt text for. */
export type MediaAltLocale = 'zh-Hant' | 'zh-Hans' | 'en'

/**
 * Normalise an i18next language onto one of the three alt locales.
 *
 * The site runs zh-Hant / zh-Hans / en, but i18next may report a regional
 * variant ('zh-HK', 'zh-CN', 'en-GB'). Anything that is not recognisably
 * Simplified or English falls back to zh-Hant — the site's primary audience
 * is Hong Kong, and Traditional is the safe default (the same rule
 * `chineseScript()` uses).
 */
export function mediaAltLocale(language: string | undefined): MediaAltLocale {
  const lang = (language ?? '').trim().toLowerCase()
  if (lang === 'zh-hans' || lang === 'zh-cn' || lang === 'zh-sg' || lang === 'zh-my') return 'zh-Hans'
  if (lang === 'en' || lang.startsWith('en-')) return 'en'
  return 'zh-Hant'
}

/**
 * The alt text of a media row for a reader in `language`.
 *
 * Empty when the row has no alt in any script — the caller decides what an
 * undescribed image should say (an article's title, an empty `alt=""` for a
 * decorative image).
 */
export function mediaAltForLocale(
  entry: Pick<CmsMediaIndexEntry, 'alt'> | null | undefined,
  language: string | undefined,
): string {
  if (!entry) return ''
  const alt = entry.alt
  return pickLocalized(
    mediaAltLocale(language),
    alt['zh-Hans'],
    alt['zh-Hant'],
    alt.en,
  )
}
